import { useEffect, useState } from 'react';
import ActivateBookForm from '../components/ActivateBookForm';
import BookShelf, { formatDate } from '../components/BookShelf';
import Modal from '../components/Modal';
import { ErrorBanner, Toast } from '../components/Feedback';
import { api } from '../api/client';

/**
 * My Library, and the activation that fills it.
 *
 * <p>An empty shelf puts the code field on the page rather than behind a button —
 * activating a book is the only thing a new student is here to do. Once there are
 * books, the same form moves into a modal so the shelf leads.
 */
export default function MyLibraryPage() {
  const [library, setLibrary] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [showActivate, setShowActivate] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api.studentLibrary().then(setLibrary).catch(setError);
  }, []);

  async function onActivated(result) {
    setShowActivate(false);
    setConfirmation(result.book);
    setToast(`${result.book.name} added to your library`);
    try {
      setLibrary(await api.studentLibrary());
    } catch (err) {
      setError(err);
    }
  }

  const books = library?.books ?? [];

  return (
    <>
      <div className="page-head row-between">
        <div>
          <h1>My Library</h1>
          <p>
            Books you have activated. Each stays in your library for 15 months from the
            day you activated it.
          </p>
        </div>
        {books.length > 0 && (
          <button type="button" className="btn btn-primary" onClick={() => setShowActivate(true)}>
            Activate a book
          </button>
        )}
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {/* Activation Confirmation — what was just added, and until when. */}
      {confirmation && (
        <div className="banner banner-info" role="status">
          <span>
            <strong>{confirmation.name}</strong> is now in your library, until{' '}
            {formatDate(confirmation.expiresAt)}.
          </span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmation(null)}>
            Dismiss
          </button>
        </div>
      )}

      {library === null && <div className="empty">Loading…</div>}

      {library !== null && books.length === 0 && (
        <section className="card card-pad" style={{ maxWidth: 460 }}>
          <h2 style={{ fontSize: 'var(--fs-md)', marginTop: 0, marginBottom: 'var(--s0)' }}>
            Activate your first book
          </h2>
          <p className="muted small" style={{ marginTop: 0 }}>
            Your teacher gives you a code for each book. Entering it here adds that book
            to your library.
          </p>
          <ActivateBookForm onActivated={onActivated} autoFocus />
        </section>
      )}

      {books.length > 0 && <BookShelf books={books} />}

      {showActivate && (
        <Modal
          title="Activate a book"
          subtitle="Enter the code your teacher gave you."
          width={460}
          onClose={() => setShowActivate(false)}
        >
          <ActivateBookForm onActivated={onActivated} autoFocus />
        </Modal>
      )}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2600)} />
    </>
  );
}
